import {
  useCallback, useState, useMemo
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button, Input, Pagination } from "@hydra/atom/components";
import { useSelector } from "react-redux";
import appSettings from "@/settings";
import { selectActiveCompany } from "@/store/appSlice";
import {
  BoxedContent,
  Header,
  SvgIcon,
  NoDataFound,
  IconButton
} from "@/components/common";

import {
  getJournalEntryColumns,
  getJournalEntryData
} from "@/components/finance/journal-entry/journalEntryTableData";
import { skeletonLoadingRows } from "@/utils/helpers";
import { getJournalEntryLists } from "@/api/finance/journalEntryApi";
import { FinanceMasterTable } from "@/components/finance";
import { useDebounce } from "@/hooks";
import { selectPermission } from "@/store/userSlice";
import staticObjectMap from "@/utils/maps/staticObjectMap";

const perPageOptions = [
  { label: "12", value: 12 },
  { label: "24", value: 24 },
  { label: "36", value: 36 },
];

function JournalEntryList() {
  const [filterText, setFilterText] = useState("");
  const activeCompany = useSelector(selectActiveCompany);
  const debouncedSearch = useDebounce(filterText, 300);
  const [perPage, setPerPage] = useState(perPageOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const hasPermission = useSelector(selectPermission({ parent: "Report", scope: staticObjectMap.get("JournalEntryObjectName"), action: "Insert" }));
  const navigate = useNavigate();
  const { isLoading, data } = useQuery(
    ["journal-entry-list", currentPage, perPage.value, debouncedSearch],
    () => getJournalEntryLists({
      pageNumber: currentPage,
      pageSize: perPage.value,
      type: "Adjusting Entry",
      "Number[like]": debouncedSearch
    })
  );

  const totalCount = useMemo(() => data?.totalCount || 0, [data?.data]);

  const handlePagination = (pageNumber, pageSize) => {
    setCurrentPage(pageNumber);
    setPerPage(pageSize);
  };

  const exportExcel = () => window.open(`${appSettings.baseUrl}/finance/reports/journal-voucher/pdf?scope=leasing&CompanyId=${activeCompany.id}`, "_blank", "noopener,noreferrer");

 const rightContent = useCallback(
    () => (
      <div className="buttons-container buttons-at-end">
        <IconButton
          className="icon-button-with-text"
          onClick={() => exportExcel()}
        >
          <SvgIcon icon="excel" />
          <span>Export</span>
        </IconButton>
        <Button
          bordered
          onClick={() => navigate("new")}
          className="btn-with-icon"
          small
        >
          <Button.Prepend>
            <SvgIcon icon="plus-icon" />
          </Button.Prepend>
          New Journal Entry
        </Button>
      </div>
    ),
    [navigate]
  );

  const renderTable = () => {
    if (!isLoading && data && !data?.data) {
      return (
        <NoDataFound
          title="No journal entry has been added"
          buttonText="Add new journal entry"
          onClick={() => navigate("new")}
        />
      );
    }

    return (
      <>
        <div className="d-flex">
          <Input
            value={filterText}
            onChange={setFilterText}
            placeholder="Search by JE number"
          />
        </div>
        <FinanceMasterTable
          isLoading={isLoading}
          tableColumns={getJournalEntryColumns(data?.data || [])}
          data={getJournalEntryData(data?.data || skeletonLoadingRows(5))}
          objectName={staticObjectMap.get("JournalEntryObjectName")}
        />
        <Pagination
          className="dashboard-pagination"
          options={perPageOptions}
          perPage={perPage}
          onSelectChange={(val) => handlePagination(currentPage, val)}
          pageRangeDisplayed={3}
          pageNo={currentPage}
          handlePageClick={(pageNo) => handlePagination(pageNo.selected + 1, perPage)}
          showResults
          offset={0}
          totalItems={totalCount}
          pageCount={Math.ceil(totalCount / perPage.value)}
          reactPaginateProps={{
            previousLabel: <span className="material-icons">&#xe5cb;</span>,
            nextLabel: <span className="material-icons">&#xe5cc;</span>,
            forcePage: currentPage - 1,
          }}
        />
      </>
    );
  };

  return (
    <BoxedContent>
      <Header
        showBreadcrumb
        leftContent={<h1>Journal Entry</h1>}
        rightContent={hasPermission ? rightContent() : ""}
      />
      {renderTable()}
    </BoxedContent>
  );
}

export default JournalEntryList;
